import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Loading } from '../components/Loading';
import { EmptyState } from '../components/EmptyState';

const STATUS_LABELS = {
  pending: { label: 'Pendente', color: 'var(--nc-warning-text)', bg: 'var(--nc-warning-soft)' },
  accepted: { label: 'Aceito', color: 'var(--nc-primary)', bg: 'var(--nc-success-soft)' },
  picked_up: { label: 'Em entrega', color: '#1e4d8c', bg: '#eff6ff' },
  delivered: { label: 'Entregue', color: 'var(--nc-success-text)', bg: 'var(--nc-success-soft)' },
  cancelled: { label: 'Cancelado', color: 'var(--nc-danger-text)', bg: 'var(--nc-danger-soft)' },
  declined: { label: 'Recusado', color: 'var(--nc-text-secondary)', bg: 'var(--nc-surface-alt)' },
};

// LE-025: estados que ainda evoluem (pendente/aceito/em entrega/recusado-em-fila).
// Pedidos entregues/cancelados/expirados são terminais — o polling para neles.
const ACTIVE_STATUSES = new Set(['pending', 'accepted', 'picked_up', 'declined']);

// LE-025: polling do status dos pedidos do cliente — atualização automática sem
// reload (pendência recorrente LE-015/16/17/18 §11: "o cliente só vê o status
// reabrindo a página"). Mesmo padrão conservador do LE-024: 30s, pausa com a
// aba oculta, nunca sobrepõe requisição em voo, refresh silencioso.
const POLL_INTERVAL_MS = 30000;

export function OrdersPage() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);
  // LE-019: estado do "Refazer pedido" (pedido expirado na fila)
  const [redoingId, setRedoingId] = useState(null);
  const [lastSync, setLastSync] = useState(null); // LE-025: "Atualizado às HH:MM"
  const [syncWarning, setSyncWarning] = useState(false); // LE-025: falha de poll silencioso
  const statusRef = useRef(null); // LE-025: status por pedido no último sync (null = 1º load)

  // LE-025: `silent` = refresh automático (sem spinner/erro em tela cheia; dados
  // antigos permanecem se a rede falhar). Ação do usuário/primeiro load = não-silent.
  const fetchOrders = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const { data } = await api.get('/orders');
      // Poll silencioso com sucesso = dados frescos — limpa erro de um load
      // inicial que tenha falhado (senão a tela ficaria presa no erro).
      if (silent) {
        setError(null);
        setSyncWarning(false);
      }
      // LE-025: marca "🆕 Atualizado" apenas pedidos cujo status MUDOU desde o
      // último sync (statusRef null no 1º load → nada é marcado como mudança).
      const nextOrders = (data.data || []).map(o => ({
        ...o,
        isUpdated: statusRef.current !== null && statusRef.current.get(o.id) !== o.status,
      }));
      setOrders(nextOrders);
      statusRef.current = new Map(nextOrders.map(o => [o.id, o.status]));
      setLastSync(new Date());
    } catch (err) {
      // Refresh automático falhou: mantém o que está na tela e informa com leveza
      if (silent) {
        setSyncWarning(true);
      } else {
        setError(err.response?.data?.error?.message || 'Erro ao carregar pedidos');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  // LE-025: polling — só enquanto houver pedidos ativos (status evolui); pausa
  // com a aba oculta; ao voltar a ficar visível, sincroniza na hora.
  const hasActiveOrders = orders.some(o => ACTIVE_STATUSES.has(o.status));
  useEffect(() => {
    if (!hasActiveOrders) return undefined;

    let inFlight = false;
    const sync = async () => {
      if (document.visibilityState === 'hidden' || inFlight) return;
      inFlight = true;
      try {
        await fetchOrders({ silent: true });
      } finally {
        inFlight = false;
      }
    };
    const onVisibility = () => { if (document.visibilityState === 'visible') sync(); };

    const timer = setInterval(sync, POLL_INTERVAL_MS);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [fetchOrders, hasActiveOrders]);

  // LE-019: refaz um pedido que expirou na fila (POST /orders/:id/redo) e
  // navega direto para o novo pedido (pendente). Erros (ex.: entregador
  // indisponível) aparecem no alerta da página.
  const handleRedo = async (orderId) => {
    setRedoingId(orderId);
    setError(null);
    try {
      const { data } = await api.post(`/orders/${orderId}/redo`);
      navigate(`/pedido/${data.order.id}`);
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Erro ao refazer pedido');
    } finally {
      setRedoingId(null);
    }
  };

  // LE-014: cancelamento de pedido pendente
  const handleCancel = async (orderId) => {
    if (!window.confirm('Cancelar este pedido de entrega?')) return;
    setCancellingId(orderId);
    setError(null);
    try {
      await api.post(`/orders/${orderId}/cancel`);
      await fetchOrders();
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Erro ao cancelar pedido');
    } finally {
      setCancellingId(null);
    }
  };

  const formatMoney = (v) => (v == null ? null : `R$ ${Number(v).toFixed(2).replace('.', ',')}`);

  return (
    <div className="nc-page">
      <div className="nc-pagehead">
        <div className="nc-row">
          <button onClick={() => navigate('/perfil')} aria-label="Voltar" className="nc-iconbtn">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" /><path d="M12 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="nc-page-title">Meus pedidos</h1>
          {/* LE-025: indicador de atualização automática (polling 30s) */}
          {lastSync && !loading && (
            <span style={{ marginLeft: 'auto', fontSize: 11, color: '#d1fae5' }}>
              🔄 Atualizado às {lastSync.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
        </div>
      </div>

      <div className="nc-container" style={{ paddingTop: 16, paddingBottom: 16 }}>
        {loading ? <Loading message="Carregando pedidos..." /> : error ? (
          <div role="alert" style={{
            padding: 14, borderRadius: 10, background: 'var(--nc-danger-soft)', border: '1px solid var(--nc-danger-border)',
            color: 'var(--nc-danger-text)', fontSize: 13, marginBottom: 12
          }}>
            {error}
            <button onClick={fetchOrders} className="nc-btn-primary" style={{ marginTop: 10, width: '100%' }}>Tentar novamente</button>
          </div>
        ) : orders.length === 0 ? (
          <EmptyState
            icon="📋"
            title="Nenhum pedido ainda"
            description="Seus pedidos de entrega aparecerão aqui."
            action={<button className="nc-btn-primary" onClick={() => navigate('/entregas')}>Ver entregadores</button>}
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {/* LE-025: falha transitória do polling silencioso — aviso neutro, não verde */}
            {syncWarning && (
              <div role="status" style={{
                padding: 10, borderRadius: 10, background: 'var(--nc-warning-soft)', border: '1px solid var(--nc-warning-border)',
                color: 'var(--nc-warning-text)', fontSize: 12, marginBottom: 4
              }}>
                ⚠️ Falha ao atualizar pedidos. Tentando novamente...
              </div>
            )}
            {orders.map(o => {
              // LE-019: pedido expirado na fila = 'cancelled' + expired_at — o
              // chip mostra "Expirado" (com aviso) em vez de "Cancelado"
              const isExpired = o.status === 'cancelled' && !!o.expired_at;
              const st = isExpired
                ? { label: 'Expirado', color: 'var(--nc-warning-text)', bg: 'var(--nc-warning-soft)' }
                : STATUS_LABELS[o.status] || STATUS_LABELS.pending;
              return (
                // LE-018: card com botão real "Ver histórico" (a11y — nada de
                // div-clicável aninhando botões; navegação por botão explícito)
                <div key={o.id} style={{ background: 'var(--nc-surface)', borderRadius: 12, border: '1px solid var(--nc-border)', padding: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--nc-text)', flex: 1 }}>
                      Pedido #{o.id}{o.business_name ? ` • ${o.business_name}` : ''}
                    </span>
                    {/* LE-025: status mudou desde o último sync (via polling). Só em
                      status ativo: se o pedido virou terminal (entregue/
                      cancelado) o polling para e não haveria novo sync para
                      limpar o flag — o chip ficaria preso para sempre. */}
                    {o.isUpdated && ACTIVE_STATUSES.has(o.status) && (
                      <span style={{
                        fontSize: 10, fontWeight: 700, color: 'var(--nc-primary)', background: '#d1fae5',
                        padding: '2px 8px', borderRadius: 10, animation: 'nc-pulse-badge 2s infinite',
                      }}>
                        🆕 Atualizado
                      </span>
                    )}
                    <span style={{ fontSize: 10, fontWeight: 600, color: st.color, background: st.bg, padding: '2px 8px', borderRadius: 10 }}>
                      {st.label}
                    </span>
                  </div>
                  <div className="nc-hint" style={{ marginBottom: 2 }}>
                    📍 {o.delivery_address}
                  </div>
                  {o.pickup_address && (
                    <div style={{ fontSize: 12, color: 'var(--nc-text-muted)', marginBottom: 2 }}>🛍️ Retirada: {o.pickup_address}</div>
                  )}
                  {o.delivery_person_name && (
                    <div style={{ fontSize: 12, color: 'var(--nc-text-muted)', marginBottom: 2 }}>🛵 Entregador: {o.delivery_person_name}</div>
                  )}
                  {/* LE-017: pedido recusado voltou para a fila — aguarda outro entregador */}
                  {o.status === 'declined' && (
                    <div style={{ fontSize: 12, color: 'var(--nc-warning-text)', marginBottom: 2 }}>
                      ✋ Aguardando outro entregador aceitar seu pedido.
                    </div>
                  )}
                  {/* LE-019: pedido expirou na fila — aviso + refazer com um toque */}
                  {isExpired && (
                    <div style={{ fontSize: 12, color: 'var(--nc-warning-text)', marginBottom: 6 }}>
                      ⏰ Nenhum entregador aceitou seu pedido a tempo e ele expirou. Refaça para tentar de novo!
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                    <span className="nc-caption">
                      {new Date(o.created_at).toLocaleDateString('pt-BR')} {new Date(o.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                    {o.total_amount != null && (
                      <span style={{ marginLeft: 'auto', fontSize: 13, fontWeight: 600, color: 'var(--nc-primary)' }}>{formatMoney(o.total_amount)}</span>
                    )}
                  </div>
                  {/* LE-019: refazer é a ação principal de um pedido expirado */}
                  {isExpired && (
                    <button
                      onClick={() => handleRedo(o.id)}
                      disabled={redoingId === o.id}
                      style={{
                        marginTop: 10, width: '100%', padding: '9px 12px', borderRadius: 8,
                        background: 'var(--nc-primary)', color: 'var(--nc-on-primary)', border: 'none',
                        fontSize: 13, fontWeight: 600,
                        cursor: redoingId === o.id ? 'wait' : 'pointer'
                      }}
                    >
                      {redoingId === o.id ? 'Refazendo...' : 'Refazer pedido'}
                    </button>
                  )}
                  {/* LE-018: botão real para o detalhe com linha do tempo */}
                  <button
                    onClick={() => navigate(`/pedido/${o.id}`)}
                    style={{
                      marginTop: 10, width: '100%', padding: '9px 12px', borderRadius: 8,
                      background: isExpired ? '#f3f4f6' : '#0d5c3b', color: isExpired ? '#374151' : '#fff',
                      border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer'
                    }}
                  >
                    Ver histórico
                  </button>
                  {/* LE-017: pendente e recusado-em-fila podem ser cancelados pelo cliente */}
                  {(o.status === 'pending' || o.status === 'declined') && (
                    <button
                      onClick={() => handleCancel(o.id)}
                      disabled={cancellingId === o.id}
                      style={{
                        marginTop: 10, width: '100%', padding: '9px 12px', borderRadius: 8,
                        background: 'var(--nc-danger-soft)', color: 'var(--nc-danger-text)', border: '1px solid var(--nc-danger-border)',
                        fontSize: 13, fontWeight: 600, cursor: cancellingId === o.id ? 'wait' : 'pointer'
                      }}
                    >
                      {cancellingId === o.id ? 'Cancelando...' : 'Cancelar pedido'}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
